import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import jwtService from '../../services/jwtService';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const isInitialized = jwtService.initialize();
        
        if (isInitialized) {
          setUser(jwtService.getUser());
          setIsAuthenticated(jwtService.isAuthenticated());
        }
      } catch (error) {
        console.error('[Auth] Dashboard initialization error:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (email, password, accountType = null) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await jwtService.login(email, password, accountType);
      
      setUser(result.user);
      setIsAuthenticated(true);
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await jwtService.register(userData);
      
      setUser(result.user);
      setIsAuthenticated(true);
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      await jwtService.logout();
      
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('[Auth] Dashboard logout error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update profile function
  const updateProfile = useCallback(async (profileData) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await jwtService.updateProfile(profileData);
      
      setUser(result.user);
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Change password function
  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await jwtService.changePassword(currentPassword, newPassword, confirmPassword);
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const result = await jwtService.getProfile();
      setUser(result.user);
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check user role
  const hasRole = useCallback((role) => {
    return jwtService.hasRole(role);
  }, []);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return jwtService.isAdmin();
  }, []);

  const value = {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
    clearError,
    
    // Helpers
    hasRole,
    isAdmin,
    
    // JWT Service access
    jwtService
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// Higher-order component for protected routes
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login page
      window.location.href = '/login';
      return null;
    }

    return <Component {...props} />;
  };
};

export default AuthContext;
